import { jwtDecode } from 'jwt-decode';

interface JWTPayload {
  exp: number;
  iat?: number;
  sub?: string;
  email?: string;
}

/**
 * Self-aware token that knows its own lifecycle state.
 *
 * Design principles:
 * - Uses wall-clock time (Date.now()) for all checks
 * - Knows when it was created, when it expires
 * - Can determine if it's expired, expiring soon, or usable
 * - Immutable - cannot be modified after creation
 */
export class Token {
  private readonly value: string;
  private readonly expiresAt: number;
  private readonly issuedAt: number;

  // 2025 Best Practice: Refresh at 75% of token lifetime
  // This provides optimal balance between performance (fewer refreshes)
  // and reliability (prevents expiry during slow network conditions)
  private static readonly REFRESH_THRESHOLD_PERCENT = 0.75;

  // Clock skew tolerance for mobile devices with incorrect clocks
  private static readonly CLOCK_SKEW_MS = 30000; // 30 seconds

  constructor(tokenString: string) {
    if (!tokenString || typeof tokenString !== 'string') {
      throw new Error('Invalid token: must be a non-empty string');
    }

    this.value = tokenString;

    try {
      const decoded = jwtDecode<JWTPayload>(tokenString);

      if (!decoded.exp) {
        throw new Error('Invalid token: missing exp claim');
      }

      // JWT exp is in seconds, convert to milliseconds
      this.expiresAt = decoded.exp * 1000;
      this.issuedAt = decoded.iat ? decoded.iat * 1000 : Date.now();

    } catch (error) {
      throw new Error(`Invalid token format: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }

  /**
   * Get the raw token value.
   * Throws if token is expired.
   */
  getValue(): string {
    if (this.isExpired()) {
      throw new Error('Cannot use expired token');
    }
    return this.value;
  }

  /**
   * Check if token is expired (with clock skew tolerance).
   *
   * Adds CLOCK_SKEW_MS buffer to handle mobile devices with incorrect clocks.
   */
  isExpired(): boolean {
    return Date.now() >= (this.expiresAt + Token.CLOCK_SKEW_MS);
  }

  /**
   * Check if token needs refresh (approaching expiry).
   *
   * Returns true when token has consumed 75% or more of its lifetime.
   * 2025 Best Practice: Refresh at 75% lifetime for optimal performance.
   *
   * Example: 1-hour token refreshes after 45 minutes (not 58 minutes)
   */
  needsRefresh(): boolean {
    const lifetime = this.expiresAt - this.issuedAt;
    const refreshThreshold = this.issuedAt + (lifetime * Token.REFRESH_THRESHOLD_PERCENT);
    return Date.now() >= refreshThreshold;
  }

  /**
   * Check if token can be used safely.
   *
   * Returns true only if:
   * - Not expired
   * - Not approaching expiry (doesn't need refresh yet)
   */
  canBeUsed(): boolean {
    return !this.isExpired() && !this.needsRefresh();
  }

  /**
   * Get time in milliseconds until token should be refreshed.
   *
   * Calculates based on 75% lifetime threshold.
   *
   * @returns Milliseconds until refresh needed, or 0 if already needs refresh
   */
  getTimeUntilRefresh(): number {
    const lifetime = this.expiresAt - this.issuedAt;
    const refreshThreshold = this.issuedAt + (lifetime * Token.REFRESH_THRESHOLD_PERCENT);
    return Math.max(0, refreshThreshold - Date.now());
  }

  /**
   * Get time in milliseconds until token expires.
   *
   * @returns Milliseconds until expiry, or 0 if already expired
   */
  getTimeUntilExpiry(): number {
    return Math.max(0, this.expiresAt - Date.now());
  }

  /**
   * Get percentage of remaining lifetime (0-100).
   *
   * Requires 'iat' (issued at) claim in token.
   * Useful for UI progress indicators.
   */
  getRemainingLifetimePercentage(): number {
    const lifetime = this.expiresAt - this.issuedAt;
    const remaining = this.getTimeUntilExpiry();
    return Math.min(100, Math.max(0, (remaining / lifetime) * 100));
  }

  /**
   * Get debug information about token state.
   */
  getDebugInfo(): {
    isExpired: boolean;
    needsRefresh: boolean;
    canBeUsed: boolean;
    timeUntilRefreshMs: number;
    timeUntilExpiryMs: number;
    remainingLifetimePercent: number;
  } {
    return {
      isExpired: this.isExpired(),
      needsRefresh: this.needsRefresh(),
      canBeUsed: this.canBeUsed(),
      timeUntilRefreshMs: this.getTimeUntilRefresh(),
      timeUntilExpiryMs: this.getTimeUntilExpiry(),
      remainingLifetimePercent: this.getRemainingLifetimePercentage()
    };
  }
}
